import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import { openSupportChatWithMessage } from "@/core/pylon/open-support-chat";

/**
 * What an owner can do when a request ends badly.
 *
 * Tell Volca opens support with the request and its failure details. A caller
 * can also offer a resend when the same words are safe to send again.
 *
 * It sits below the message rather than inside it. The words are what the
 * owner said; this is what became of them, and they are not the same thing.
 */
export const TurnFailure = ({
  note,
  request,
  detail,
  onResend,
  disabled,
  className,
}: {
  /** What happened, in the owner's terms. Left out when the reply said it. */
  note?: string;
  /** The words they asked for, sent again as they are. */
  request: string;
  /** The same thing said to support, with the part they should not have to explain. */
  detail: string;
  onResend?: (request: string) => void;
  disabled: boolean;
  /** Lets the caller hang it under a right-aligned bubble. */
  className?: string;
}) => (
  // A dot in a reserved gutter, the way a row that needs attention is marked
  // everywhere else here, rather than a banner or a badge. It is the only red
  // in the conversation, so it does not have to be loud to be found.
  <Stack
    direction="row"
    gap={0}
    align="start"
    className={cn("flex items-start gap-2 pt-1 text-xs", className)}
  >
    <span
      aria-hidden
      className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-danger-bold"
    />
    <Stack gap={0} align="start" className="flex min-w-0 flex-col gap-1">
      {!!note && <p className="text-subtle">{note}</p>}
      <Stack direction="row" gap={1} align="center" className="flex gap-3">
        {!!request && onResend && (
          <Button
            size="xs"
            variant="ghost"
            className="h-auto p-0"
            disabled={disabled}
            onClick={() => onResend(request)}
          >
            Send it again
          </Button>
        )}
        <Button
          size="xs"
          variant="ghost"
          className="h-auto p-0"
          onClick={() =>
            openSupportChatWithMessage(
              `${detail}\n\nWhat I asked for:\n${request}`,
            )
          }
        >
          Tell Volca
        </Button>
      </Stack>
    </Stack>
  </Stack>
);
